import { Component, computed, inject, input, output } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { InnogyComponentRendering } from '@core/jss-models';
import type { StreetAddress } from '@essent/new-customer';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import { ConsumptionValuesVM } from '@innogy/become-a-customer/shared/interfaces';
import type { QuestionnaireResults } from '@retention/shared';
import { type CurrentContract } from '@innogy/retention/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AutomationIdDirective } from '@innogy/utils-automation-id';

import type { CommodityOfferContent } from '../commodity-offers.model';
import { getSpecificOfferValueFromOffer } from '../utils';
import { CommodityOfferHeaderComponent } from '../commodity-offer-header/commodity-offer-header.component';
import { EssentCurrentContractCardComponent } from '../current-contract-brand/essent-current-contract/essent-current-contract-card.component';
import { CommodityOfferCalculationBasedOnComponent } from '../commodity-offer-calculation-based-on/commodity-offer-calculation-based-on.component';
import { CommodityBestOfferListComponent } from '../commodity-best-offer-list/commodity-best-offer-list.component';
import { CommodityOfferListComponent } from '../commodity-offer-list/commodity-offer-list.component';
import { HorizontalCommodityOfferFooterComponent } from '../horizontal-commodity-offer-footer/horizontal-commodity-offer-footer.component';

@Component({
  selector: 'wl-horizontal-commodity-offer',
  templateUrl: './horizontal-commodity-offer.component.html',
  standalone: true,
  imports: [
    CommodityOfferHeaderComponent,
    EssentCurrentContractCardComponent,
    CommodityOfferCalculationBasedOnComponent,
    TranslatePipe,
    CommodityBestOfferListComponent,
    AutomationIdDirective,
    CommodityOfferListComponent,
    HorizontalCommodityOfferFooterComponent,
  ],
})
export class HorizontalCommodityOfferComponent {
  readonly envConfig = inject(ENVIRONMENT_CONFIG);

  readonly questionnaireResults = input<QuestionnaireResults | undefined>();
  readonly offers = input<Offer[]>();
  readonly rendering = input<InnogyComponentRendering>();
  readonly content = input<CommodityOfferContent>();
  readonly showMemberGetMember = input<boolean>(false);
  readonly consumptionValues = input<ConsumptionValuesVM>();
  readonly supplyAddress = input<Partial<StreetAddress>>();
  readonly accountId = input<string>();
  readonly flowId = input<string>();
  readonly currentContract = input<CurrentContract>();
  readonly currentContractLoading = input<boolean>();
  readonly hasSolarPanels = input<boolean>();

  readonly bestOfferId = computed(() =>
    this.questionnaireResults() ? 'best-offer' : undefined
  );

  readonly collapseEssentCurrentOfferCard = computed(
    () => !!this.questionnaireResults()?.highestMatch
  );

  selectOffer = output<Offer>();
  openMoreInfo = output<Offer>();
  viewTariffs = output<Offer>();
  adjustCalculation = output<void>();
  showQuestionnaireExplanation =
    output<ReturnType<typeof getSpecificOfferValueFromOffer>>();
  selectVariableOffer = output();

  /**
   * The current contract component should be shown if:
   * 1. It is enabled by feature flag and available for the segment/brand
   * 2. It is loading or it finished loading and is available
   */
  readonly shouldShowCurrentContractComponent = computed((): boolean => {
    const featureEnabled =
      !!this.content()?.showMyContract &&
      this.envConfig.segment === 'consument' &&
      this.envConfig.brand === 'essent';
    const isLoading = this.currentContractLoading();
    const currentContract = this.currentContract();

    return featureEnabled && (isLoading || !!currentContract);
  });
}
